import type { Currency, CurrencyExposure, Deltas, PnL, TimeWindow } from '../../shared/types';
import { fmtPct, fmtSignedPct } from '../../shared/format';
import { useMask } from '../lib/mask';
import { usePrivacy } from '../lib/contexts';
import type { Scope } from '../lib/scope';
import { Icons } from './Icon';
import './../styles/components/hero.css';

const WINDOW_KEY: Record<TimeWindow, keyof Deltas> = {
  '1D': 'd1',
  '7D': 'd7',
  '30D': 'd30',
  '90D': 'd90',
  '1Y': 'y1',
  '2Y': 'y2',
};
const WINDOW_LABEL: Record<TimeWindow, string> = {
  '1D': 'Last 1d',
  '7D': 'Last 7d',
  '30D': 'Last 30d',
  '90D': 'Last 90d',
  '1Y': 'Last 1y',
  '2Y': 'Last 2y',
};

type Props = {
  totalUsd: number;
  totalEur: number;
  tradableUsd: number;
  tradableEur: number;
  weightedRisk: number;
  deltas: Deltas;
  pnl: PnL;
  currencyExposure: CurrencyExposure[];
  ccy: Currency;
  win: TimeWindow;
  scope: Scope;
  onScopeChange: (scope: Scope) => void;
};

export function HeroTotal({
  totalUsd,
  totalEur,
  tradableUsd,
  tradableEur,
  weightedRisk,
  deltas,
  pnl,
  currencyExposure,
  ccy,
  win,
  scope,
  onScopeChange,
}: Props) {
  const { amount } = useMask();
  const { hidden, toggle: togglePrivacy } = usePrivacy();
  const showAll = scope === 'total';
  const value = showAll
    ? ccy === 'USD' ? totalUsd : totalEur
    : ccy === 'USD' ? tradableUsd : tradableEur;
  const d = deltas[WINDOW_KEY[win]];
  const dAbs = ccy === 'USD' ? d.abs_usd : d.abs_eur;
  const sign = d.pct >= 0 ? 'gain' : 'loss';
  const hasPnl = pnl.coverage > 0 && pnl.cost_basis_usd > 0;
  const pnlAbs = ccy === 'USD' ? pnl.abs_usd : pnl.abs_eur;
  const pnlSign = pnl.pct >= 0 ? 'gain' : 'loss';
  const sortedExposure = currencyExposure.slice().sort((a, b) => b.share - a.share);

  return (
    <section class="hero">
      <button
        type="button"
        class="hero-label hero-label-toggle mono"
        onClick={() => onScopeChange(showAll ? 'liquid' : 'total')}
        aria-pressed={showAll}
        title={showAll ? 'Show liquid only' : 'Include illiquid assets'}
      >
        <span>{showAll ? 'Total Net Worth · incl. illiquid' : 'Liquid Net Worth'}</span>
        <Icons.RefreshCw size={11} class="hero-label-icon" aria-hidden="true" />
      </button>
      <div class="hero-value-row">
        <button
          type="button"
          class={`hero-value hero-value-toggle mono ${hidden ? 'is-hidden' : ''}`}
          onClick={togglePrivacy}
          aria-pressed={hidden}
          aria-label={hidden ? 'Show amounts' : 'Hide amounts'}
          title={hidden ? 'Show amounts' : 'Hide amounts'}
        >
          {amount(value, ccy)}
        </button>
      </div>

      <dl class="hero-stats">
        <div class="hero-stat">
          <dt class="hero-stat-label mono">{WINDOW_LABEL[win]} Change</dt>
          <dd class="hero-stat-body">
            <span class={`hero-pill ${sign}`}>
              <span>{amount(dAbs, ccy, { signed: true })}</span>
              <span>{fmtSignedPct(d.pct)}</span>
            </span>
          </dd>
        </div>

        {hasPnl && (
          <div class="hero-stat">
            <dt class="hero-stat-label mono">
              All-time P&amp;L
              {pnl.coverage < 0.999 && (
                <span class="hero-stat-sub">
                  &nbsp;· {Math.round(pnl.coverage * 100)}% tracked
                </span>
              )}
            </dt>
            <dd class="hero-stat-body">
              <span class={`hero-pill ${pnlSign}`}>
                <span>{amount(pnlAbs, ccy, { signed: true })}</span>
                <span>{fmtSignedPct(pnl.pct)}</span>
              </span>
            </dd>
          </div>
        )}

        <div class="hero-stat">
          <dt class="hero-stat-label mono">Weighted Risk</dt>
          <dd class="hero-stat-value mono">
            {weightedRisk.toFixed(1)}
            <span class="hero-stat-sub">&nbsp;/ 10</span>
          </dd>
        </div>

        {sortedExposure.length > 0 && (
          <div class="hero-stat">
            <dt class="hero-stat-label mono">Currency Exposure</dt>
            <dd class="hero-stat-value mono hero-stat-ccy">
              {sortedExposure.map((c, i) => (
                <span class="hero-ccy-item" key={c.ccy}>
                  {i > 0 && <span class="hero-stat-sub">·&nbsp;</span>}
                  <span>{c.ccy}</span>
                  <span class="hero-stat-sub">&nbsp;{fmtPct(c.share, { decimals: 0 })}</span>
                </span>
              ))}
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}
